/**
 * AgreementSlotList — the rows of one area's instruments, and every ceremony they can open.
 *
 * SPLIT OUT OF `AgreementsBoard`, WHICH IS RETIRED. The board was one screen that owned the model,
 * the witness, the rows and the six ceremony dialogs at once; the Dossier needs those rows in
 * three different places (contract instruments on the agreements canvas, the insurance loan beside
 * its loan, the deposit agreement beside its money) drawn from ONE model that the page holds. So
 * the model stayed in `useContractAgreements`, the witness moved into the Parties block where it
 * is a party rather than a setting, and what is left — rows plus ceremonies — is this.
 *
 * THE CEREMONIES ARE STILL MODAL, AND THEY ARE NOW FIVE [D-B49]. Reviewing, sending,
 * countersigning, issuing a signing link and voiding are evidence collection, not editing: the
 * token RPCs compare against the hash the admin was SHOWN, so the surface that shows it has to be
 * the surface that signs. Every mutation below is byte-for-byte the board's own, including the
 * no-fallback-render rule on `issueToken` — re-rendering at submit time would attest to text
 * nobody read and sign straight through the drift `AGREEMENT_CONTENT_CHANGED` exists to catch.
 *
 * THE SIXTH WAS RECORDING A PAPER ORIGINAL, AND THERE IS NO LONGER A ROUTE FOR IT. `authenticated`
 * and `anon` have lost INSERT and UPDATE on `signed_at`, `agreement_url` and `origin` across all
 * five legacy arcs, so every statement that sheet composed answers `42501` naming a relation and
 * neither the column nor the rule. The originals already on file are untouched and still open
 * every gate they used to; nothing new is filed, from here or anywhere else.
 *
 * SENDING IS TWO DIALOGS, NOT ONE [D-B48]. The Vehicle Schedule asks for a figure this schema does
 * not hold; every other instrument is generated from what the contract already states.
 *
 * THE ONE MUTATION THAT HAS GROWN SINCE IS THE VOID [D-B44]. Every other executed instrument that
 * quotes the hire purchase agreement's reference number in frozen text goes with it: one confirm,
 * one typed reason, the master and its dependents withdrawn in board order — and reissued in that
 * same order where a reissue was asked for. The list of what cites what is not written here — it is
 * `citationCascade`, which the money door on the Financials canvas reads through the same call —
 * and WHICH INSTRUMENTS ARE PUT TO IT is `cascadeSlots`, the contract's whole junction chain rather
 * than the scope the mounting page happens to be about [D-B45].
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { openSignedUrl } from '@/lib/openSignedUrl';

import AgreementDocumentRow, { type AgreementRowActions } from './AgreementDocumentRow';
import AgreementReviewSheet from './AgreementReviewSheet';
import CountersignDialog from './CountersignDialog';
import GeneratePartnerScheduleDialog from './GeneratePartnerScheduleDialog';
import SendForSignatureDialog from './SendForSignatureDialog';
import SigningLinkDialog from './SigningLinkDialog';
import VoidReissueDialog from './VoidReissueDialog';
import { reissueRefusalFor, sealStateFor, slotKey, type BoardSlot, type SealState } from './boardSlots';
import { reportAgreementError, type useContractAgreements } from './useContractAgreements';
import { useAgreementSealingWithFallback } from './useAgreementSealing';
import {
    countersignAgreement,
    createAgreement,
    issueWitnessToken,
    previewAgreementPdf,
    renderAgreement,
    revokeToken,
    saveBlob,
    sweepOrphans,
    voidAgreement,
    type AgreementRenderPayload,
    type IssuedToken,
} from '@/lib/agreements/api';
import {
    citationCascade,
    INSTRUMENT_NOUNS,
    instrumentsFromSlots,
    signatureText,
    type InstrumentFact,
} from '@/components/contracts/setup/hazards';
import { COUNTERSIGN_CONSENT_VERSION, type AgreementDocType } from '@/lib/agreements/constants';
import { liveTokenFor } from '@/lib/agreements/tokens';

/** Refusal shown when no render is on screen for the document a link was asked for. */
const NO_SHOWN_HASH_MESSAGE =
    'Open Review on this document first. The witness link attests to the exact text you were ' +
    'shown, so it cannot be issued against a hash this page has not displayed.';

export type ContractAgreementsModel = ReturnType<typeof useContractAgreements>;

interface Props {
    /*
     * NO `contractId` HERE ANY MORE [D-B49]. Its one consumer was the paper-filing sheet, which
     * composed `{ contract_id, signed_at, agreement_url, origin }` — and `authenticated` has lost
     * the write on the last three of those columns on all five arcs, so the sheet, its mount and
     * this prop are gone together. Everything else this list needs about the contract reaches it
     * through `agreements`, which is the page's one signing model.
     */
    /** The slots this list draws — one area's share of `partitionSlotsByStep`. */
    slots: BoardSlot[];
    /** The page's one signing model. Never re-declared here. */
    agreements: ContractAgreementsModel;
    /**
     * Routes a merge-field pre-flight to the area that can fill the field.
     *
     * THE FIELD ITSELF IS THE SECOND ARGUMENT, for a host whose areas are finer than
     * `MERGE_FIELD_STEPS`'s vocabulary — the policy dossier splits the one `insurance` step across
     * three of them. Optional, so every host that routes by step alone is unchanged.
     */
    onDeepLink: (area: string, field?: string) => void;
    /** Drawn when the area has no instruments of its own. */
    emptyText?: string;
}

export default function AgreementSlotList({
    slots,
    agreements,
    onDeepLink,
    emptyText = 'Nothing to sign here yet.',
}: Props) {
    const queryClient = useQueryClient();

    const [reviewingId, setReviewingId] = useState<number | null>(null);
    const [sendingSlot, setSendingSlot] = useState<BoardSlot | null>(null);
    const [countersigningSlot, setCountersigningSlot] = useState<BoardSlot | null>(null);
    const [voidingSlot, setVoidingSlot] = useState<BoardSlot | null>(null);
    const [witnessLinkSlot, setWitnessLinkSlot] = useState<BoardSlot | null>(null);
    const [issuedToken, setIssuedToken] = useState<IssuedToken | null>(null);
    const [exportingKey, setExportingKey] = useState<string | null>(null);

    const {
        driverName,
        witnessName,
        documents,
        /**
         * THE WHOLE CONTRACT, NOT THIS LIST'S SHARE OF IT AND NOT THIS PAGE'S SCOPE EITHER.
         *
         * The `slots` prop is one area's partition, and the insurance advance a hire purchase
         * reissue invalidates sits in another one — a cascade computed off the prop would silently
         * miss it. `agreements.slots` fixed that and left a second gap behind it [D-B44, D-B45]:
         * on the setup canvas that board is the id-earliest junction's [D-B17], so a sealed RENEWAL
         * year's Record or Advance — reachable since the Insurance tab gained its door — was not in
         * the census either, and the void would report it had carried everything.
         *
         * `cascadeSlots` is the same board read at full junction width, for this question alone.
         * Nothing renders it and nothing grades it; see its derivation in `useContractAgreements`.
         */
        cascadeSlots,
        tokens,
        signingProfile,
        signerName,
        signerRole,
        templateGap,
        instrumentGap,
        refreshBoard,
    } = agreements;

    /**
     * ONE LATCH FOR THE WHOLE PAGE. The page provides it, so two lists and a deposit block
     * mounted at once share the single finalize attempt rather than racing each other into
     * `AGREEMENT_FINALIZE_IN_PROGRESS` — which the second caller then reads as a failed seal
     * needing a human. The fallback keeps this component usable on its own.
     */
    const sealing = useAgreementSealingWithFallback({ documents, onRefresh: refreshBoard });

    // The hash the countersigning admin is shown, and the one their signature attests to.
    const { data: countersignRender } = useQuery({
        queryKey: ['agreement-render', countersigningSlot?.document?.id],
        queryFn: () => renderAgreement(countersigningSlot!.document!.id),
        enabled: !!countersigningSlot?.document,
    });

    const fail = useCallback((error: unknown) => reportAgreementError(error), []);

    const shownHash = useCallback(
        (documentId: number): string | null =>
            queryClient.getQueryData<AgreementRenderPayload>(['agreement-render', documentId])
                ?.content_sha256 ?? null,
        [queryClient],
    );

    // ── Mutations ────────────────────────────────────────────────────────────
    const generate = useMutation({
        /**
         * THE SAME TWO VARIABLES ON EVERY DOC TYPE, THE VEHICLE SCHEDULE INCLUDED [D-B59]. The
         * schedule's Cash price is the contract's own, copied by `agreement_create` at generation,
         * so nothing the dialog shows travels with the gesture.
         */
        mutationFn: ({ docType, subjectId }: { docType: AgreementDocType; subjectId: number }) =>
            createAgreement(docType, subjectId),
        onSuccess: (result) => {
            toast.success(result.created ? 'Document generated and sent' : 'Document already exists');
            setSendingSlot(null);
            refreshBoard();
        },
        onError: fail,
    });

    const countersign = useMutation({
        mutationFn: ({
            documentId,
            typedName,
            contentSha256,
        }: { documentId: number; typedName: string; contentSha256: string }) =>
            countersignAgreement({
                documentId,
                contentSha256,
                typedName,
                signaturePath: signingProfile?.signature_path as string,
                consentVersion: COUNTERSIGN_CONSENT_VERSION,
            }),
        onSuccess: () => {
            toast.success('Countersigned');
            setCountersigningSlot(null);
            refreshBoard();
        },
        onError: fail,
    });

    const issueToken = useMutation({
        mutationFn: async (documentId: number) => {
            // NO FALLBACK RENDER. Issuing the link freezes the text, so the hash has to be the
            // one already on screen.
            const hash = shownHash(documentId);
            if (!hash) throw new Error(NO_SHOWN_HASH_MESSAGE);
            return await issueWitnessToken(documentId, hash);
        },
        onSuccess: (token) => {
            setIssuedToken(token);
            refreshBoard();
        },
        onError: (error) => {
            // This page refusing itself is a state, not a fault worth reporting.
            if (error instanceof Error && error.message === NO_SHOWN_HASH_MESSAGE) {
                toast.error(NO_SHOWN_HASH_MESSAGE);
                return;
            }
            fail(error);
        },
    });

    const revoke = useMutation({
        mutationFn: (tokenId: number) => revokeToken(tokenId, 'Revoked from the signing board'),
        onSuccess: () => {
            toast.success('Signing link revoked');
            setIssuedToken(null);
            refreshBoard();
        },
        onError: fail,
    });

    /**
     * What WITHDRAWING this document takes with it [D-B44].
     *
     * THE HIRE PURCHASE AGREEMENT ALONE HAS DEPENDENTS, and they are dependents of its REFERENCE
     * NUMBER: `witness v2` quotes `contract.hire_purchase_reference` twice and both advance
     * schedules three times each, in text that is already frozen. Take the master away and those
     * documents are left naming an instrument nobody can produce — the state [D-B43]'s runbook had
     * to repair by hand, in a mandated order, in the same sitting.
     *
     * NOT GATED ON `reissue`, AND THE GATE WAS THE BUG. This read `reissue ? cascade : []` on the
     * reasoning that a pure withdrawal mints no new reference, so the citations still resolve.
     * They do not: what they resolve to IS the master, and the withdrawal is what kills it. A hire
     * purchase agreement voided without reissue on contract 319 left the witness agreement and the
     * insurance advance quoting a reference no document carries — exactly the state the cascade
     * exists to prevent, reached through the other half of the same dialog.
     *
     * SO THE VOID HALF FOLLOWS THE MASTER AND THE REISSUE HALF FOLLOWS THE BOX. The citation dies
     * with the master either way; the checkbox chooses whether replacements are minted, never
     * whether the stale paper survives.
     *
     * THE RULE ITSELF LIVES IN `../contracts/setup/hazards`, where a truth table tests it and the
     * money door reads the same function — one dependency list, two doors.
     */
    const cascadeFor = useCallback(
        (slot: BoardSlot | null): InstrumentFact[] => {
            if (slot?.docType !== 'HIRE_PURCHASE') return [];
            return citationCascade({ instruments: instrumentsFromSlots(cascadeSlots) }).voidable;
        },
        [cascadeSlots],
    );

    const voidingDependents = useMemo(
        () => cascadeFor(voidingSlot),
        [cascadeFor, voidingSlot],
    );

    const voidDocument = useMutation({
        /**
         * EVERY VOID BEFORE ANY CREATE, and the master's create before its dependents'.
         *
         * The partial unique index behind each arc admits one non-VOIDED document per subject, so
         * an interleaved create hands back the document it was meant to replace. The master goes
         * first among the creates because `get_my_pending_signatures` orders the driver's list by
         * `created_at` — an advance offered ahead of the hire purchase agreement is a seat
         * `agreement_sign` refuses with `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`.
         *
         * ONE REASON FOR THE WHOLE ACT. The admin typed one sentence about one decision, and it is
         * filed against every document that decision withdrew.
         *
         * AND THE VOID LOOP IS UNCONDITIONAL WHILE THE CREATE LOOP IS NOT. The dependents cite a
         * reference this withdrawal destroys whether or not a replacement is minted, so they go
         * with the master every time; `reissue` decides only whether anything is generated in
         * their place.
         */
        mutationFn: async ({
            slot,
            reason,
            reissue,
        }: { slot: BoardSlot; reason: string; reissue: boolean }) => {
            const carried = cascadeFor(slot);

            /*
             * NOTHING IS WITHDRAWN THAT THE REISSUE CANNOT REPLACE [D-B59]. A Vehicle Schedule over
             * a contract that states no cash price voids cleanly and is then refused its
             * replacement, so the refusal is asked before the first void rather than learned after
             * the last. The dialog holds its confirm shut on the same sentence.
             */
            const refusal = reissue ? reissueRefusalFor(slot) : null;
            if (refusal) throw new Error(refusal);

            await voidAgreement(slot.document!.id, reason || null);
            for (const dependent of carried) {
                if (dependent.documentId === null) continue;
                await voidAgreement(dependent.documentId, reason || null);
            }

            if (reissue) {
                /*
                 * A REISSUED SCHEDULE STATES THE CONTRACT'S CURRENT CASH PRICE [D-B59]. The void
                 * above leaves the referent unsealed with no live document, and `agreement_create`
                 * re-copies `contracts.cash_price` onto it in exactly that state, so no figure
                 * travels with this call on any doc type. The dependents are the citation
                 * cascade's — a witness agreement and the insurance instruments.
                 */
                await createAgreement(slot.docType, slot.subjectId);
                for (const dependent of carried) {
                    await createAgreement(dependent.docType, dependent.subjectId);
                }
            }

            /**
             * WHAT THE TOAST COUNTS, RETURNED RATHER THAN RE-DERIVED. `onSuccess` calls
             * `refreshBoard`, so a second `cascadeFor` there would be answering off a board that
             * is about to move; this is the number this call actually withdrew. It is the
             * dialog's own count — the master plus its dependents — so the confirm an admin read
             * and the toast they get back agree.
             */
            return { withdrawn: carried.length + 1 };
        },
        onSuccess: (result, variables) => {
            toast.success(
                result.withdrawn > 1
                    ? variables.reissue
                        ? `Voided and reissued ${result.withdrawn} documents`
                        : `Voided ${result.withdrawn} documents`
                    : variables.reissue
                      ? 'Voided and reissued'
                      : 'Document voided',
            );
            setVoidingSlot(null);
            refreshBoard();
            // The bytes the withdrawals stranded, once for the gesture. Fire and forget: it never
            // throws, reports nothing, and the next void's sweep drains whatever this one missed.
            sweepOrphans();
        },
        onError: (error, { slot, reissue }) => {
            // This page refusing itself is a state, not a fault worth reporting.
            if (reissue && error instanceof Error && error.message === reissueRefusalFor(slot)) {
                toast.error(error.message);
                return;
            }
            fail(error);
        },
    });

    // ── Row actions ──────────────────────────────────────────────────────────
    const exportPdf = async (slot: BoardSlot) => {
        if (!slot.document) return;
        setExportingKey(slot.key);
        try {
            const blob = await previewAgreementPdf(slot.document.id, false);
            saveBlob(blob, `${slot.document.reference_no}-preview.pdf`);
        } catch (error) {
            fail(error);
        } finally {
            setExportingKey(null);
        }
    };

    const downloadFinal = async (slot: BoardSlot | undefined) => {
        const doc = slot?.document;
        if (!doc?.final_bucket || !doc.final_path) return;
        const { data, error } = await supabase.storage
            .from(doc.final_bucket)
            .createSignedUrl(doc.final_path, 3600);
        if (error || !data?.signedUrl) {
            fail(error ?? new Error('Executed copy unavailable'));
            return;
        }
        window.open(data.signedUrl, '_blank');
    };

    /** PARTY-SCOPED, NOT DOCUMENT-SCOPED — a deposit document can carry a DRIVER token as well. */
    const activeTokenFor = (slot: BoardSlot) => liveTokenFor(tokens, slot.document?.id, 'WITNESS');

    const rowActions = (slot: BoardSlot): AgreementRowActions => ({
        onSend: () => setSendingSlot(slot),
        onReview: () => setReviewingId(slot.document?.id ?? null),
        onCountersign: () => setCountersigningSlot(slot),
        onWitnessLink: () => {
            setIssuedToken(null);
            setWitnessLinkSlot(slot);
        },
        onVoid: () => setVoidingSlot(slot),
        onRetrySeal: () => slot.document && sealing.retrySeal(slot.document.id),
        onDownloadFinal: () => downloadFinal(slot),
        onExportPdf: () => exportPdf(slot),
        onOpenLegacy: () => openSignedUrl(slot.legacyUrl),
        onDeepLink,
    });

    const rowBusy = (slot: BoardSlot): 'send' | 'export' | null => {
        if (exportingKey === slot.key) return 'export';
        // Matched on the whole slot identity: HIRE_PURCHASE and WITNESS are both generated from
        // the contract, so a subject-only match spins the row nobody clicked as well.
        const generating = generate.variables;
        if (
            generate.isPending &&
            generating &&
            slotKey(generating.docType, generating.subjectId) === slot.key
        ) {
            return 'send';
        }
        return null;
    };

    const sealState = (slot: BoardSlot): SealState =>
        sealStateFor(slot.document, sealing.attemptFor(slot.document?.id));

    return (
        <div className="space-y-3">
            {slots.length === 0 ? (
                <p className="sd-hint">{emptyText}</p>
            ) : (
                slots.map((slot) => (
                    <SlotRow
                        key={slot.key}
                        slot={slot}
                        busy={rowBusy(slot)}
                        seal={sealState(slot)}
                        actions={rowActions(slot)}
                        templateGap={templateGap(slot)}
                        instrumentGap={instrumentGap(slot)}
                    />
                ))
            )}

            <AgreementReviewSheet
                documentId={reviewingId}
                onOpenChange={(open) => !open && setReviewingId(null)}
                onDeepLink={onDeepLink}
                onDownloadFinal={() =>
                    downloadFinal(slots.find((candidate) => candidate.document?.id === reviewingId))
                }
            />

            {/* TWO CONFIRMATIONS, BECAUSE ONE OF THEM STATES A FIGURE [D-B59]. Every other
                instrument's dialog names the channel each party is reached on and nothing else.
                The Vehicle Schedule states the contract's cash price as its Cash price, copied at
                generation, and `agreement_create` refuses the document where the contract has none,
                so that row's dialog shows the figure it will state and holds Generate shut
                without one. */}
            {sendingSlot?.docType === 'PARTNER_MANAGEMENT' ? (
                <GeneratePartnerScheduleDialog
                    open
                    onOpenChange={(open) => !open && setSendingSlot(null)}
                    title={sendingSlot.title}
                    referenceNo={sendingSlot.document?.reference_no ?? null}
                    cashPrice={sendingSlot.cashPrice}
                    isPending={generate.isPending}
                    onConfirm={() =>
                        generate.mutate({
                            docType: sendingSlot.docType,
                            subjectId: sendingSlot.subjectId,
                        })
                    }
                />
            ) : sendingSlot ? (
                <SendForSignatureDialog
                    open
                    onOpenChange={(open) => !open && setSendingSlot(null)}
                    docType={sendingSlot.docType}
                    title={sendingSlot.title}
                    referenceNo={sendingSlot.document?.reference_no ?? null}
                    driverName={driverName}
                    witnessName={witnessName}
                    isPending={generate.isPending}
                    onConfirm={() =>
                        generate.mutate({
                            docType: sendingSlot.docType,
                            subjectId: sendingSlot.subjectId,
                        })
                    }
                />
            ) : null}

            {countersigningSlot?.document && (
                <CountersignDialog
                    open
                    onOpenChange={(open) => !open && setCountersigningSlot(null)}
                    documentTitle={countersigningSlot.document.title}
                    referenceNo={countersigningSlot.document.reference_no}
                    signerName={signerName}
                    signerRole={signerRole}
                    signaturePath={signingProfile?.signature_path ?? null}
                    contentSha256={countersignRender?.content_sha256 ?? null}
                    isPending={countersign.isPending}
                    onConfirm={(typedName) =>
                        countersign.mutate({
                            documentId: countersigningSlot.document!.id,
                            typedName,
                            contentSha256: countersignRender!.content_sha256,
                        })
                    }
                />
            )}

            {voidingSlot?.document && (
                <VoidReissueDialog
                    open
                    onOpenChange={(open) => !open && setVoidingSlot(null)}
                    documentTitle={voidingSlot.document.title}
                    referenceNo={voidingSlot.document.reference_no}
                    requiresReason={voidingSlot.document.status === 'COMPLETED'}
                    dependents={voidingDependents.map((dependent) => ({
                        noun: INSTRUMENT_NOUNS[dependent.docType],
                        state: signatureText(dependent),
                    }))}
                    /* THE FIGURE THE PARTNER'S RETURNS ARE MEASURED AGAINST, NAMED [D-B59]. The
                       replacement re-copies the contract's cash price, which may have moved since
                       the withdrawn schedule copied it, so the box says which figure the new one
                       states rather than leave "the current contract values" to cover it. */
                    reissueNote={
                        voidingSlot.docType === 'PARTNER_MANAGEMENT'
                            ? 'The replacement states the contract’s current Cash price.'
                            : null
                    }
                    /* AND WHERE THE CONTRACT STATES NONE, THE REISSUE IS REFUSED BEFORE THE VOID
                       [D-B59] — the sentence the mutation would throw, shown before the click. */
                    reissueRefusal={reissueRefusalFor(voidingSlot)}
                    isPending={voidDocument.isPending}
                    onConfirm={(reason, reissue) =>
                        voidDocument.mutate({ slot: voidingSlot, reason, reissue })
                    }
                />
            )}

            {witnessLinkSlot?.document && (
                <SigningLinkDialog
                    open
                    onOpenChange={(open) => {
                        if (!open) {
                            setWitnessLinkSlot(null);
                            setIssuedToken(null);
                        }
                    }}
                    party="WITNESS"
                    recipientName={witnessName}
                    issued={issuedToken}
                    activeToken={activeTokenFor(witnessLinkSlot)}
                    isIssuing={issueToken.isPending}
                    isRevoking={revoke.isPending}
                    onIssue={() => issueToken.mutate(witnessLinkSlot.document!.id)}
                    onRevoke={() => {
                        const token = activeTokenFor(witnessLinkSlot);
                        if (token) revoke.mutate(token.id);
                    }}
                />
            )}
        </div>
    );
}

/**
 * A row plus its own pre-flight read.
 *
 * `missing[]` comes from `agreement_render`, one call per document. Fetching it here keeps it
 * lazy per row and lets React Query share the key with the review sheet and the countersign
 * dialog — and it is that shared cache entry `shownHash` reads, so what the page displays is what
 * a signature attests to.
 */
function SlotRow({
    slot,
    busy,
    seal,
    actions,
    templateGap,
    instrumentGap,
}: {
    slot: BoardSlot;
    busy: 'send' | 'export' | null;
    seal: SealState;
    actions: AgreementRowActions;
    templateGap: ReturnType<ContractAgreementsModel['templateGap']>;
    instrumentGap: ReturnType<ContractAgreementsModel['instrumentGap']>;
}) {
    const { data: render } = useQuery({
        queryKey: ['agreement-render', slot.document?.id],
        queryFn: () => renderAgreement(slot.document!.id),
        enabled: !!slot.document,
    });

    return (
        <AgreementDocumentRow
            slot={slot}
            missing={render?.missing ?? null}
            busy={busy}
            seal={seal}
            actions={actions}
            templateGap={templateGap}
            instrumentGap={instrumentGap}
        />
    );
}
